import React, { useState } from 'react';
import { trackConversion, trackMetaEvent, CONVERSIONS } from '../../lib/gtag';
import { Loader2, Send, CheckCircle2, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/** Flotten-Anfrage ab 201 Fahrzeugen — direkt aus dem Konfigurator/Drawer. */
export const FleetInquiryForm = ({ vehicles, annualKm, light = false }) => {
  const [form, setForm] = useState({
    company: '', contact_name: '', email: '', phone: '',
    vehicles: Math.max(vehicles || 201, 201), message: '',
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const inputCls = light
    ? 'w-full px-3.5 py-2.5 rounded-full bg-white border border-stone-300 text-sm text-stone-900 placeholder:text-stone-500 outline-none focus:border-stone-900 transition-colors'
    : 'w-full px-3.5 py-2.5 rounded-full bg-white/10 border border-white/15 text-sm text-white placeholder:text-stone-500 outline-none focus:border-emerald-400 transition-colors';

  const submit = async () => {
    if (!form.company.trim() || !form.contact_name.trim() || !form.email.trim()) {
      setError('Bitte Firma, Ansprechpartner und E-Mail ausfüllen.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/contact/fleet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company: form.company,
          contact_name: form.contact_name,
          email: form.email,
          phone: form.phone,
          vehicles: Math.max(parseInt(form.vehicles, 10) || 201, 201),
          annual_km: annualKm || 0,
          message: form.message,
        }),
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      setDone(data.reference);
      trackConversion(CONVERSIONS.LEAD_FORM, {
        userData: {
          email: form.email,
          phone: form.phone,
          firstName: (form.contact_name || '').split(' ')[0],
          lastName: (form.contact_name || '').split(' ').slice(1).join(' '),
          country: 'DE',
        },
      });
      trackMetaEvent('Lead', { params: { value: 1.0, currency: 'EUR' }, email: form.email });
    } catch {
      setError('Anfrage konnte nicht gesendet werden — bitte erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className={`rounded-2xl p-5 text-center ${light ? 'bg-emerald-50 border border-emerald-200' : 'bg-emerald-500/10 border border-emerald-400/30'}`} data-testid="fleet-inquiry-success">
        <CheckCircle2 className={`w-8 h-8 mx-auto mb-2 ${light ? 'text-emerald-600' : 'text-emerald-400'}`} strokeWidth={2} />
        <p className={`text-sm font-medium ${light ? 'text-stone-900' : 'text-white'}`}>Flotten-Anfrage gesendet — Referenz {done}</p>
        <p className={`text-xs mt-1 ${light ? 'text-stone-500' : 'text-stone-400'}`}>Unser Flotten-Team meldet sich schnellstmöglich mit deinem individuellen Angebot.</p>
      </div>
    );
  }

  return (
    <div data-testid="fleet-inquiry-form">
      <p className={`text-xs mb-3 flex items-start gap-1.5 ${light ? 'text-stone-500' : 'text-stone-400'}`}>
        <Building2 className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${light ? 'text-amber-500' : 'text-yellow-400'}`} strokeWidth={2} />
        Ab 201 Fahrzeugen erstellen wir dir ein individuelles Flotten-Angebot — sende uns deine Anfrage direkt hier:
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <input value={form.company} onChange={(e) => set('company', e.target.value)} placeholder="Firma *" className={inputCls} data-testid="fleet-company" />
        <input value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} placeholder="Ansprechpartner *" className={inputCls} data-testid="fleet-contact" />
        <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="E-Mail *" className={inputCls} data-testid="fleet-email" />
        <input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="Telefon" className={inputCls} data-testid="fleet-phone" />
        <input type="number" min="201" value={form.vehicles} onChange={(e) => set('vehicles', e.target.value)} placeholder="Anzahl Fahrzeuge *" className={inputCls} data-testid="fleet-vehicles" />
        <input value={form.message} onChange={(e) => set('message', e.target.value)} placeholder="Nachricht (optional)" className={inputCls} data-testid="fleet-message" />
      </div>
      {error && <p className={`text-[11px] mb-2 ${light ? 'text-rose-600' : 'text-rose-400'}`} data-testid="fleet-error">{error}</p>}
      <button
        onClick={submit}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-yellow-400 text-stone-900 text-sm font-semibold hover:bg-yellow-300 transition-colors disabled:opacity-60"
        data-testid="fleet-submit"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Send className="w-4 h-4" strokeWidth={2} />}
        Flotten-Anfrage senden
      </button>
    </div>
  );
};
